'use client';

import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReferenceCategory, ReferenceCategorySlug } from '@/lib/reference';

// Lightweight term shape passed from the server (no heavy sections payload).
export type ReferenceCardTerm = {
  slug: string;
  term: string;
  category: ReferenceCategorySlug;
  shortDefinition: string;
  aliases?: string[];
  platforms: string[];
};

const firstLetter = (term: ReferenceCardTerm) => {
  const c = term.term.trim()[0]?.toUpperCase() ?? '#';
  return /[A-Z]/.test(c) ? c : '#';
};

function Card({ term, label }: { term: ReferenceCardTerm; label?: string }) {
  return (
    <Link className="ref-card" data-category={term.category} href={`/reference/${term.slug}`}>
      <span className="ref-card-head">
        <span className="ref-card-badge" aria-hidden="true">
          {firstLetter(term)}
        </span>
        <span className="ref-card-cat">{label}</span>
      </span>
      <span className="ref-card-name">{term.term}</span>
      <span className="ref-card-def">{term.shortDefinition}</span>
      {term.platforms.length ? (
        <span className="ref-card-tags">
          {term.platforms.map((p) => (
            <span className="ref-tag" key={p}>
              {p}
            </span>
          ))}
        </span>
      ) : null}
    </Link>
  );
}

export default function ReferenceExplorer({
  terms,
  categories,
}: {
  terms: ReferenceCardTerm[];
  categories: ReferenceCategory[];
}) {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<ReferenceCategorySlug | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const labelOf = useMemo(
    () => new Map(categories.map((c) => [c.slug, c.label] as const)),
    [categories],
  );

  // "/" focuses search from anywhere; Escape clears.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
      if (e.key === '/' && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.key === 'Escape' && typing && el === inputRef.current) {
        setQuery('');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const isFiltering = q.length > 0 || activeCategory !== null;

  const results = useMemo(() => {
    let list = terms;
    if (activeCategory) list = list.filter((t) => t.category === activeCategory);
    if (q) {
      list = list.filter((t) => {
        const hay = `${t.term} ${(t.aliases ?? []).join(' ')} ${t.shortDefinition} ${
          labelOf.get(t.category) ?? ''
        }`.toLowerCase();
        return hay.includes(q);
      });
    }
    return [...list].sort((a, b) => a.term.localeCompare(b.term));
  }, [terms, q, activeCategory, labelOf]);

  const grouped = useMemo(
    () =>
      categories
        .map((category) => ({
          category,
          items: terms
            .filter((t) => t.category === category.slug)
            .sort((a, b) => a.term.localeCompare(b.term)),
        }))
        .filter((g) => g.items.length > 0),
    [terms, categories],
  );

  const letters = useMemo(
    () => [...new Set([...terms].sort((a, b) => a.term.localeCompare(b.term)).map(firstLetter))],
    [terms],
  );

  return (
    <div className="ref-explorer">
      <div className="ref-search">
        <label className="tool-app-catalog-search ref-search-box">
          <svg viewBox="0 0 24 24" aria-hidden="true" fill="none">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.75" />
            <path d="M16 16l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
          <span className="sr-only">Search the reference</span>
          <input
            ref={inputRef}
            type="search"
            inputMode="search"
            placeholder={`Search ${terms.length} storage & file terms…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the reference"
          />
          {query ? (
            <button
              className="tool-app-catalog-clear"
              onClick={() => setQuery('')}
              aria-label="Clear"
              type="button"
            >
              Clear
            </button>
          ) : (
            <kbd className="ref-search-kbd" aria-hidden="true">
              /
            </kbd>
          )}
        </label>

        <div
          className="tool-app-catalog-filter-row ref-filters"
          role="group"
          aria-label="Filter by category"
        >
          <button
            className={`tool-app-catalog-filter${
              activeCategory === null ? ' tool-app-catalog-filter-active' : ''
            }`}
            type="button"
            onClick={() => setActiveCategory(null)}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              data-category={c.slug}
              className={`tool-app-catalog-filter${
                activeCategory === c.slug ? ' tool-app-catalog-filter-active' : ''
              }`}
              type="button"
              onClick={() => setActiveCategory(activeCategory === c.slug ? null : c.slug)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {isFiltering ? (
        <section className="ref-section" aria-label="Search results">
          <p className="ref-count">
            {results.length} {results.length === 1 ? 'term' : 'terms'}
            {activeCategory ? ` in ${labelOf.get(activeCategory)}` : ''}
            {q ? ` matching “${query.trim()}”` : ''}
          </p>
          {results.length ? (
            <div className="ref-grid">
              {results.map((t) => (
                <Card term={t} label={labelOf.get(t.category)} key={t.slug} />
              ))}
            </div>
          ) : (
            <div className="ref-empty">
              <p>No terms match “{query.trim()}”.</p>
              <button className="ref-action-link" onClick={() => setQuery('')}>
                Clear search
              </button>
            </div>
          )}
        </section>
      ) : (
        <>
          <nav className="ref-az" aria-label="Jump to letter">
            {letters.map((l) => (
              <a className="ref-az-link" href={`#letter-${l}`} key={l}>
                {l}
              </a>
            ))}
          </nav>

          {grouped.map(({ category, items }) => (
            <section className="ref-section" data-category={category.slug} key={category.slug}>
              <div className="ref-section-head">
                <h2>
                  {category.label} <span className="ref-section-count">{items.length}</span>
                </h2>
                <p>{category.description}</p>
              </div>
              <div className="ref-grid">
                {items.map((t) => (
                  <Card term={t} label={category.label} key={t.slug} />
                ))}
              </div>
            </section>
          ))}

          <section className="ref-section" aria-label="All terms A to Z">
            <div className="ref-section-head">
              <h2>A–Z index</h2>
              <p>Every term in the reference, alphabetically.</p>
            </div>
            <div className="ref-index">
              {letters.map((l) => (
                <div className="ref-index-group" id={`letter-${l}`} key={l}>
                  <span className="ref-index-letter">{l}</span>
                  <ul>
                    {[...terms]
                      .sort((a, b) => a.term.localeCompare(b.term))
                      .filter((t) => firstLetter(t) === l)
                      .map((t) => (
                        <li key={t.slug}>
                          <Link href={`/reference/${t.slug}`}>{t.term}</Link>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
